import React from 'react';

// API/Components
import {
	ANALYSIS_TYPE,
	STATE_COLOR,
	AnalysisResultToStateColor,
	evaluateRiskIndex,
	buildMeasurementText,
	measUnitsConversion,
	userMetricUnits
} from './enums';

// Material UI
import Backdrop from '@mui/material/Backdrop';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

// Determine if the user is using metric or imperial units
const tempMeasurementUnits = userMetricUnits() ? '°C' : '°F';

// Border Coloring
const borderStyle = '1px solid #D8D7D6';

// Failed String Color
const failingStringColor = STATE_COLOR.CRITICAL + 'aa';

// Column headers and widths for the Strings Summary table
const colHeaders = ['#', 'Voltage', 'Current', 'Avg Temp', 'Avg Imped', 'Avg Imped Increase', 'Replaced Units', 'Install Date', 'Risk Index'];
const colWidths = ['5%', '15%', '15%', '10%', '10%', '10%', '10%', '10%', '10%'];

export default function StringsSummary({ stringAnalysisData, loading = false }) {

	// Default table data structure
	let tableData = [
		{
			stringNum: '',
			rowColor: 'inherit',
			voltage: { measText: '--.- V', outText: '' },
			voltageColor: 'inherit',
			current: { measText: '--.- A', outText: '' },
			currentColor: 'inherit',
			avgTemp: '--.-',
			avgImped: '-.---',
			avgImpedIncrease: '--.-',
			replacedUnits: { number: '--', percentage: '--' },
			installDate: '--/--/----',
			riskIndex: 'Unknown'
		}
	];

	if (stringAnalysisData && stringAnalysisData.length > 0) {

		// Function to render a single string's summary
		function SingleStringSummary(stringData) {
			const stringNum = stringData.string_num;
			const voltage = stringData.voltage;
			const current = stringData.current;
			const avgTemp = stringData.stats.avg_unit_temp ? measUnitsConversion(stringData.stats.avg_unit_temp).toFixed(1) : '--.-';
			const avgImped = stringData.stats.avg_unit_imped ?? '-.---';
			const avgImpedIncrease = stringData.stats.avg_unit_imped_increase ?? '--.-';
			const replacedUnits = stringData.stats.num_units_replaced;
			const installDate = new Date(stringData.stats.install_date).toLocaleString(undefined, { month: 'short', year: 'numeric' });
			const riskIndex = evaluateRiskIndex(stringData.risk_index.risk_index, stringData.risk_index.risk_flags);
			const failingUnit = (stringData.stats.failing_unit_pos_half === 1) || (stringData.stats.failing_unit_neg_half === 1);

			const voltageText = buildMeasurementText(voltage.measurement, voltage.analysis_valid, voltage.analysis_out, voltage.analysis_result, ANALYSIS_TYPE.VOLTAGE, 1, 2, '---.-');
			const voltageAnalysisColor = AnalysisResultToStateColor(voltage.analysis_result) + 'aa';

			const currentText = buildMeasurementText(current.measurement, current.analysis_valid, current.analysis_out, current.analysis_result, ANALYSIS_TYPE.CURRENT);
			const currentAnalysisColor = AnalysisResultToStateColor(current.analysis_result) + 'aa';

			// Update row, voltage and current colors based on failing unit status
			let rowColor = failingUnit ? failingStringColor : ( stringNum % 2 === 1 ? 'rgba(0, 0, 0, 0.05)' : 'inherit');
			let voltageColor = failingUnit ? 'none' : voltageAnalysisColor;
			let currentColor = failingUnit ? 'none' : currentAnalysisColor;

			return ({
				stringNum: stringNum,
				rowColor: rowColor,
				voltage: voltageText,
				voltageColor: voltageColor,
				current: currentText,
				currentColor: currentColor,
				avgTemp: avgTemp,
				avgImped: avgImped,
				avgImpedIncrease: avgImpedIncrease,
				replacedUnits: { number: replacedUnits.number, percentage: replacedUnits.percentage },
				installDate: installDate,
				riskIndex: riskIndex
			});
		}

		tableData = stringAnalysisData.map((string) => SingleStringSummary(string));
	}

	return (
		<Card style={{ border: '1px solid #D8D7D6', borderRadius: '5px', boxShadow: '2px 2px 4px gray', px: 0 }}>
			<Box sx={{ backgroundColor: 'lightgray', p: 1 }}>
				<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif', fontWeight: 'bold' }}>
					Strings Summary
				</Typography>
			</Box>
			<CardContent style={{ padding: 0, position: 'relative' }}>
				{loading && (
					<Backdrop
						sx={{
							color: '#fff',
							zIndex: (theme) => theme.zIndex.drawer + 1,
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							borderRadius: 'inherit',
							backgroundColor: 'rgba(0,0,0,0.5)'
						}}
						open={true}
					>
						<CircularProgress color="inherit" />
					</Backdrop>
				)}
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								{colHeaders.map((col, i) => (
									<TableCell
										key={i}
										align="center"
										sx={{
											fontFamily: 'Saira,sans-serif',
											fontWeight: 'bold',
											textAlign: 'center',
											borderRight: (i + 1 === colHeaders.length) ? 'None' : borderStyle,
											p: 0,
										}}
									>
										{col}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{tableData.map((string, i) => ( 
								<TableRow key={string.stringNum} sx={{ backgroundColor: string.rowColor }}>
									<TableCell align="center" width={colWidths[0]} sx={{ borderRight: borderStyle, p: 1 }} >
										{string.stringNum}
									</TableCell>
									<TableCell align="center" width={colWidths[1]} sx={{ backgroundColor: string.voltageColor, borderRight: borderStyle, p: 0 }}>
										{string.voltage.measText}DC {string.voltage.outText}
									</TableCell>
									<TableCell align="center" width={colWidths[2]} sx={{ backgroundColor: string.currentColor, borderRight: borderStyle, p: 0 }}>
										{string.current.measText} {string.current.outText}
									</TableCell>
									<TableCell align="center" width={colWidths[3]} sx={{ borderRight: borderStyle, p: 0, }}>
										{string.avgTemp} {tempMeasurementUnits}
									</TableCell>
									<TableCell align="center" width={colWidths[4]} sx={{ borderRight: borderStyle, p: 0, }} >
										{string.avgImped} mΩ
									</TableCell>
									<TableCell   align="center" width={colWidths[5]} sx={{ borderRight: borderStyle, p: 0,  }} >
										{string.avgImpedIncrease} %
									</TableCell>
									<TableCell align="center" width={colWidths[6]} sx={{ borderRight: borderStyle, p: 0, }} >
										{string.replacedUnits.number} ({string.replacedUnits.percentage}%)
									</TableCell>
									<TableCell align="center" width={colWidths[7]} sx={{ borderRight: borderStyle, p: 0, }} >
										{string.installDate}
									</TableCell>
									<TableCell align="center" width={colWidths[8]} sx={{ p: 0, }} >
										{string.riskIndex}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</CardContent>
		</Card>
	);
}

		